import { useMemo, useState } from 'react';
import type { DirectoryEntry } from '@stfors/shared-types';
import { useDirectory } from '../api/queries/directory';
import { AddIndividualModal } from './AddIndividualModal';
import './OrgPicker.css';

export interface PersonPick {
  personId: number;
  displayName: string;
}

interface PersonPickerProps {
  value: number | null;
  onChange: (personId: number | null, person: PersonPick | null) => void;
  disabled?: boolean;
  /** Hide people already linked (e.g. current company contacts). */
  excludePersonIds?: number[];
  /** Shown when no selection. */
  placeholder?: string;
  /**
   * Name to display while `value` is null — for a field that was free text
   * before it had an FK behind it (`projects.lead_attorney`), so opening the
   * form doesn't appear to have blanked what's stored. Clearing it is still an
   * `onChange(null, null)`; the caller decides what that means for the text.
   */
  fallbackLabel?: string | null;
  /**
   * Offer "Other" at the foot of the list, which opens the directory's
   * add-person window and selects whoever it creates. Off by default — a
   * picker used to LINK an existing person (company contacts) shouldn't
   * invite a new one.
   */
  allowCreate?: boolean;
}

/**
 * Typeahead over directory individuals (staff excluded by the directory API
 * default view). Modeled on <OrgPicker>.
 */
export function PersonPicker({
  value,
  onChange,
  disabled = false,
  excludePersonIds = [],
  placeholder = 'Search individuals…',
  fallbackLabel = null,
  allowCreate = false,
}: PersonPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Keep last selected name so the input still shows it after query clears.
  const [pickedName, setPickedName] = useState<string | null>(null);
  const dirQ = useDirectory({ q: query });
  const exclude = useMemo(() => new Set(excludePersonIds), [excludePersonIds]);

  const people = useMemo(() => {
    const entries = dirQ.data?.entries ?? [];
    return entries.filter(
      (e): e is DirectoryEntry & { person_id: number } =>
        e.entry_type === 'individual'
        && e.person_id !== null
        && !exclude.has(e.person_id),
    );
  }, [dirQ.data?.entries, exclude]);

  const selectedLabel = useMemo(() => {
    if (value === null) return fallbackLabel || null;
    const hit = people.find((p) => p.person_id === value);
    return hit?.display_name ?? pickedName;
  }, [value, people, pickedName, fallbackLabel]);

  const inputValue = open ? query : (selectedLabel ?? '');
  // There is something to clear whenever the field READS as filled, which
  // includes a fallback label standing in for an id we never had.
  const showClear = selectedLabel !== null;

  const choose = (e: DirectoryEntry | null) => {
    if (!e || e.person_id === null) {
      onChange(null, null);
      setPickedName(null);
    } else {
      onChange(e.person_id, { personId: e.person_id, displayName: e.display_name });
      setPickedName(e.display_name);
    }
    setQuery('');
    setOpen(false);
  };

  return (
    <div className={`op-root${disabled ? ' op-disabled' : ''}`}>
      <div className="op-input-wrap">
        <input
          type="text"
          className="form-input op-input"
          value={inputValue}
          placeholder={dirQ.isPending && query.length === 0 ? 'Loading…' : placeholder}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onChange={(ev) => {
            setQuery(ev.target.value);
            setOpen(true);
          }}
          disabled={disabled}
          aria-label="Individual"
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {showClear && !open && (
          <button
            type="button"
            className="op-clear"
            onClick={() => choose(null)}
            aria-label="Clear individual"
            disabled={disabled}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="op-options" role="listbox">
          {people.length === 0 && (
            <li className="op-empty">
              {dirQ.isPending ? 'Loading…' : `No individuals match "${query}"`}
            </li>
          )}
          {people.map((p) => (
            <li key={p.person_id} role="option" aria-selected={p.person_id === value}>
              <button
                type="button"
                className="op-option"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => choose(p)}
              >
                <span className="op-option-name">{p.display_name}</span>
                {p.roles.length > 0 && (
                  <span className="op-option-dba">{p.roles.join(' · ')}</span>
                )}
              </button>
            </li>
          ))}

          {allowCreate && (
            <li className="op-add-row">
              <button
                type="button"
                className="op-add"
                // The list closes on blur; preventing mousedown's default keeps
                // the click from being eaten by that.
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => { setOpen(false); setAddOpen(true); }}
              >
                Other — add someone new…
              </button>
            </li>
          )}
        </ul>
      )}

      {addOpen && (
        <AddIndividualModal
          open
          onClose={() => setAddOpen(false)}
          // Selected immediately, because opening this window IS the act of
          // choosing them — the typeahead's own list may not have refetched yet,
          // so the name is carried over rather than looked up.
          onCreated={(personId, displayName) => {
            onChange(personId, { personId, displayName });
            setPickedName(displayName);
            setQuery('');
            setAddOpen(false);
          }}
        />
      )}
    </div>
  );
}
